import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useWidget } from "../../../../providers/widget"
import { useFetchWidget } from "../../../../hooks"
import { useExplorer } from "../../providers"
import { useFetchProperties, withBrowsePriceField, usePropertyPins, withPinsFilters, PropertyPin } from "../../data"
import { ExploreWidgetConfig } from "../../config"
import { ExploreProperty } from "../../data"
import { getMarkerBadgeHtml, getPopupHtml, resolveInfowindowIcons } from "./MapMarker"
import { getProxiedImageUrl } from "../../../../common/image-proxy"
import { useLeaflet, useLeafletMarkercluster } from "../../../../common/map"
import { noDatesLabel } from "../../../../common/property-card/no-dates-label"
import { resolvePropertyLink, withStayParams } from "../../../../common/property-href"
import { usePropertyHrefFeed } from "../../../../common/use-property-href-feed"
import { resolveLeafletTileConfig } from "./tile-layer"
import { useMapSearchControls, MapBoundsBox } from "./useMapSearchControls"
import MapChrome from "./MapChrome"
import { applyZoomDamping } from "./zoomDamping"
import { hoverPanLocked } from './hoverPanLock'
import { beginMapDragGuard } from './mapDragGuard'
import {
  resolveStacking, stackingCssVars, buildStackGroups, parseStackKey,
  getPriceRangeText, getSpiderOffsets, clusterIconHtml, stackedBadgeHtml,
} from './mapStacking'
import { createSpiderfyController, SpiderfyController } from './spiderfyController'
import { resolveAllPins, extraPinsFor, dotPinHtml, pinCardHtml, DOT_PIN_SIZE } from './allPins'
import { useMobileResults } from '../mobileResults/context'
import { boundsApplied, initialSearchBounds } from './initialView'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
declare const L: any

function debounce(fn: () => void, ms: number) {
  let timer: ReturnType<typeof setTimeout>
  return () => {
    clearTimeout(timer)
    timer = setTimeout(fn, ms)
  }
}

type MarkerEntry = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  marker: any
  property: ExploreProperty
}

export default function LeafletMap() {
  const { options, feedId, widgetId } = useWidget<ExploreWidgetConfig>()
  const { data: widgetData } = useFetchWidget(feedId, widgetId)
  const pdpRoute = widgetData?.feed?.additional_info?.reverseProxy?.routes?.pdp || '/property/[slug]'
  const hrefFeed = usePropertyHrefFeed(widgetData?.feed)
  const { state, registerMapActions } = useExplorer()
  // Mobile results sheet geometry (#856) — the frozen inactive value outside the sheet layout.
  const mobileResults = useMobileResults()
  const platformUuid = options.filter?.platform
  const mapContainerRef = useRef<HTMLDivElement>(null)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const mapRef = useRef<any>(null)
  const markersRef = useRef<MarkerEntry[]>([])
  const activePopupRef = useRef<HTMLDivElement | null>(null)
  const currentMarkerIndexRef = useRef<number>(-1)
  const programmaticPanRef = useRef(false)
  // Generation token for the suppression flag: each programmatic operation
  // bumps it when arming, and the viewport effect's bounded fallback clear
  // only fires if NO newer operation armed in between — otherwise a pending
  // 700ms timer could strip a fresh fitBounds/pan suppression and let its
  // debounced moveend read as a user move (#858 round 5).
  const programmaticPanSeqRef = useRef(0)
  const armProgrammaticPan = useCallback(() => {
    programmaticPanSeqRef.current++
    programmaticPanRef.current = true
  }, [])
  const leafletLoaded = useLeaflet()

  // Toolkit map-stacking port (#854): cluster-primary (Leaflet.markercluster)
  // or co-located stacked badges + spiderfy. All state that must survive a
  // marker rebuild rides refs; the spider controller itself is rebuilt with the
  // markers so its closures never go stale.
  const stacking = useMemo(() => resolveStacking(options.map?.stacking), [options.map?.stacking])
  const clusterReady = useLeafletMarkercluster(stacking.clusterMode, leafletLoaded)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const clusterGroupRef = useRef<any>(null)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const stackMarkersRef = useRef<Map<string, any>>(new Map())
  const stackGroupsRef = useRef<Map<string, string[]>>(new Map())
  const memberStackKeyRef = useRef<Map<string, string>>(new Map())
  const spiderRef = useRef<SpiderfyController | null>(null)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const fanRef = useRef<{ key: string; leaves: { marker: any; id: string }[]; legs: any[] } | null>(null)
  // Marker the open popup is anchored to — a plain pin, a clustered pin, a
  // spider leaf (whose display position differs from its property's anchor),
  // or an all-pins dot.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const popupAnchorRef = useRef<any>(null)
  // Pixel offset from the anchor the open popup was placed at — the full
  // property popup and the compact all-pins card are different sizes, and the
  // move/zoom reposition must keep whichever offset the popup opened with (#855).
  const popupOffsetRef = useRef<{ x: number; y: number }>({ x: 150, y: 250 })
  // Toolkit map-all-pins port (#855): dot markers for the full filtered set
  // beyond the loaded page.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const extraMarkersRef = useRef<any[]>([])
  // One-shot fallback to OSM when Mapbox tiles fail asynchronously (revoked
  // token, missing style scope — those only surface as 401/404 tile errors).
  // Keyed to the exact failing config so an edited provider/token/style gets a
  // fresh chance instead of staying pinned to OSM for the component's life.
  const [failedTileConfig, setFailedTileConfig] = useState<string | null>(null)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const tileLayerRef = useRef<any>(null)

  const searchControls = useMapSearchControls()
  // The init effect runs once per map instance; route its handlers through a
  // ref so they always see the current control callbacks (stale-closure fix).
  const searchControlsRef = useRef(searchControls)
  searchControlsRef.current = searchControls

  const getBoundsBox = useCallback((): MapBoundsBox | null => {
    if (!mapRef.current) return null
    const bounds = mapRef.current.getBounds()
    return {
      north: bounds.getNorth(),
      south: bounds.getSouth(),
      east: bounds.getEast(),
      west: bounds.getWest(),
    }
  }, [])

  const mapConfig = options.map ?? { provider: 'leaflet', searchOnMove: true, zoomMin: 0, zoomMax: 18, placement: 'right', showPrice: true }
  // Component-scope so the marker effect can depend on it: with `properties`
  // memoized, an options-only re-render no longer re-runs the effect by
  // accident, so badge inputs must be tracked explicitly (#1243 review).
  const showPrice = mapConfig.showPrice && options.priceDisplay.noDates !== 'none'
  // #1188: the pin/popup "No dates available" verdict from the widget's parsed
  // priceDisplay — null = operator opt-out (legacy price pin). Component-scope
  // for the same reason as showPrice.
  const noDates = noDatesLabel(options)

  // Use the same filters (including map bounds) as PropertyGrid so they share the same react-query cache
  const boundsString = state.mapSearchActive && state.mapVisible && state.mapBounds
    ? `${state.mapBounds.west},${state.mapBounds.south},${state.mapBounds.east},${state.mapBounds.north}`
    : undefined

  const filtersWithBounds = useMemo(() => {
    const filters = { ...state.visitorFilters }
    if (boundsString) {
      filters.map_bounds = boundsString
    }
    return withBrowsePriceField(filters, options)
  }, [state.visitorFilters, boundsString, options])

  const { data } = useFetchProperties(feedId, filtersWithBounds, options.filter.limit, platformUuid)
  // Memoized on the query data: the marker effect keys on this array's
  // identity, and a bare derivation rebuilt every marker on ANY re-render of
  // the tree — 24 marker mutations per sheet snap measured in #1242 (#1243).
  const properties = useMemo(() => data?.pages.flatMap((page) => page.result) ?? [], [data])

  // Toolkit map-all-pins port (#855): the FULL filtered set as skinny pins.
  // Never bounds-scoped — withPinsFilters strips map_bounds (and sort_by), so
  // search-as-move panning narrows the cards but never the pin set.
  const allPins = useMemo(() => resolveAllPins(options.map?.allPins), [options.map?.allPins])
  const pinsFilters = useMemo(
    () => withPinsFilters(state.visitorFilters, options),
    [state.visitorFilters, options],
  )
  const dated = !!(state.visitorFilters.checkin && state.visitorFilters.checkout)
  const cardsFirstPage = data?.pages?.[0]?.result
  // Dated queries run the heavy availability/pricing work server-side and the
  // pins mirror repeats it — fired concurrently they stampede the backend
  // twice per search (the toolkit's review-verified failure mode). Serialize:
  // dated pins fetch only after the card request SUCCEEDED with results.
  // Undated queries fetch immediately (cheap, shared-cacheable).
  const pinsQueryEnabled = allPins.enabled
    && (!dated || (cardsFirstPage !== undefined && cardsFirstPage.length > 0))
  const { data: pinsData } = usePropertyPins(feedId, pinsFilters, platformUuid, { enabled: pinsQueryEnabled })
  // A successful dated response with ZERO cards resolves the pin set as empty
  // without a mirror fetch: an empty grid must not sit beside the previous
  // filter's map. Memoized — the marker effect keys on this reference.
  const pins = useMemo<PropertyPin[]>(() => {
    if (!allPins.enabled) return []
    if (dated && cardsFirstPage !== undefined && cardsFirstPage.length === 0) return []
    return pinsData?.pins ?? []
  }, [allPins.enabled, dated, cardsFirstPage, pinsData])

  // Initialize map
  useEffect(() => {
    if (!leafletLoaded || !mapContainerRef.current || mapRef.current) return

    const map = L.map(mapContainerRef.current, {
      minZoom: mapConfig.zoomMin,
      maxZoom: mapConfig.zoomMax,
      zoom: mapConfig.zoomMin,
      center: [39.8283, -98.5795],
      zoomControl: false, // We use custom controls
      // OSM's license (and Mapbox's ToS) require visible attribution; the tile
      // layer effect below supplies the provider-correct string (#852).
      attributionControl: true,
    })
    map.attributionControl.setPrefix(false)

    mapRef.current = map

    // A remount while search-as-move is active (the mobile results sheet
    // swaps the renderer's parent across its breakpoint, #856) opens on the
    // active bounds instead of the default camera — the marker effect will
    // not fit while bounds exist. Programmatic: its moveend is not a gesture.
    const seed = initialSearchBounds(state)
    if (seed) {
      programmaticPanRef.current = true
      programmaticPanSeqRef.current += 1
      // Same band inset as fitBounds: on a sheet remount the area must land
      // above the sheet, not under it (codex r2).
      const band = mobileResults.getFramePadding(mapContainerRef.current)
      map.fitBounds([[seed.south, seed.west], [seed.north, seed.east]], {
        animate: false,
        ...(band ? { paddingTopLeft: [band.left, band.top], paddingBottomRight: [band.right, band.bottom] } : {}),
      })
    }

    // Momentum-zoom damping (toolkit map-polish parity) — OPT-OUT: absent or
    // true = damped, explicit false disables. Per the #852 absence rule the
    // fallback lives here at the consumer, never as a schema default.
    if (options.map?.zoomDamping !== false) applyZoomDamping(map)

    // Leaflet needs a size recalc after the container is fully laid out
    setTimeout(() => map.invalidateSize(), 100)

    // Map position change handlers — search-active state and the pill state
    // machine are read through searchControlsRef (stale-closure fix, #852).
    const handlePositionChange = debounce(() => {
      if (programmaticPanRef.current) {
        programmaticPanRef.current = false
        return
      }
      if (!mapRef.current) return
      const bounds = mapRef.current.getBounds()
      searchControlsRef.current.onMoveEnd({
        north: bounds.getNorth(),
        south: bounds.getSouth(),
        east: bounds.getEast(),
        west: bounds.getWest(),
      })
    }, 500)

    map.on('zoomend', handlePositionChange)
    map.on('moveend', handlePositionChange)

    // Genuine-user-gesture wiring (never programmatic framing): Leaflet only
    // fires dragstart for real drags; wheel/pinch arrive as container DOM
    // events. Zoom buttons call onUserGesture from their own handlers.
    const container = mapContainerRef.current
    const notifyGesture = () => {
      // A genuine gesture supersedes any pending programmatic suppression
      // (e.g. the invalidateSize window) — its moveend must not be discarded.
      programmaticPanRef.current = false
      searchControlsRef.current.onUserGesture()
    }
    map.on('dragstart', notifyGesture)
    const onWheel = () => notifyGesture()
    const onTouchMove = (e: TouchEvent) => {
      if (e.touches.length >= 2) notifyGesture()
    }
    container.addEventListener('wheel', onWheel, { passive: true })
    container.addEventListener('touchmove', onTouchMove, { passive: true })

    // Update tooltip position on map move/zoom
    map.on('move', updatePopupPosition)
    map.on('zoom', updatePopupPosition)

    return () => {
      container.removeEventListener('wheel', onWheel)
      container.removeEventListener('touchmove', onTouchMove)
      spiderRef.current?.dispose()
      spiderRef.current = null
      map.remove() // removes every layer, the cluster group included
      clusterGroupRef.current = null
      mapRef.current = null
      tileLayerRef.current = null
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leafletLoaded])

  // Tile layer lives in its own effect so live edits to provider/token/style
  // swap the layer instead of stranding the one created at init (#852).
  const tileConfigSignature = `${options.map?.tileProvider ?? ''}|${options.map?.mapboxPublicToken ?? ''}|${options.map?.mapboxStyle ?? ''}`
  const resolvedTileProvider = resolveLeafletTileConfig(options.map, failedTileConfig === tileConfigSignature).provider
  useEffect(() => {
    if (!leafletLoaded || !mapRef.current) return
    const config = resolveLeafletTileConfig(options.map, failedTileConfig === tileConfigSignature)
    const layer = L.tileLayer(config.url, { attribution: config.attribution })
    if (config.provider === 'mapbox') {
      layer.once('tileerror', () => setFailedTileConfig(tileConfigSignature))
    }
    if (tileLayerRef.current) {
      tileLayerRef.current.off('tileerror')
      tileLayerRef.current.remove()
    }
    layer.addTo(mapRef.current)
    tileLayerRef.current = layer
  }, [leafletLoaded, tileConfigSignature, failedTileConfig]) // eslint-disable-line react-hooks/exhaustive-deps

  // Close active popup
  const closePopup = useCallback(() => {
    if (activePopupRef.current) {
      activePopupRef.current.remove()
      activePopupRef.current = null
    }
    currentMarkerIndexRef.current = -1
    popupAnchorRef.current = null
    // Remove focus class from all badges (stacked ones included)
    markersRef.current.forEach(({ marker }) => {
      const el = marker._icon as HTMLElement | undefined
      if (el) el.classList.remove('hfse-map-badge-parent-focus')
    })
    stackMarkersRef.current.forEach((marker) => {
      const el = marker._icon as HTMLElement | undefined
      if (el) el.classList.remove('hfse-map-badge-parent-focus')
    })
  }, [])

  // Update popup position to track its anchor marker on map move/zoom. The
  // anchor is a live marker reference (not an index) so spider leaves — whose
  // display position is the fan target, not the property's coordinate — track
  // correctly too (#854).
  const updatePopupPosition = useCallback(() => {
    if (!activePopupRef.current || !mapRef.current || !popupAnchorRef.current) return
    const markerPoint = mapRef.current.latLngToContainerPoint(popupAnchorRef.current.getLatLng())
    activePopupRef.current.style.left = `${markerPoint.x - popupOffsetRef.current.x}px`
    activePopupRef.current.style.top = `${markerPoint.y - popupOffsetRef.current.y}px`
  }, [])

  // Fit bounds with 10% margin (matching WP plugin)
  const fitBounds = useCallback(() => {
    if (!mapRef.current) return
    const entries = markersRef.current
    if (entries.length === 0) return
    // fitBounds is ALWAYS programmatic framing — its moveend must never be
    // read as a user position change. Without this, a marker-refresh fit that
    // lands between a first gesture and its debounced moveend would write the
    // automatic framing into the active search bounds (#858 fresh pass 2).
    armProgrammaticPan()

    const bounds = L.latLngBounds()
    entries.forEach(({ property }) => {
      if (property.location?.lat && property.location?.lng) {
        bounds.extend([property.location.lat, property.location.lng])
      }
    })

    if (!bounds.isValid()) return

    // 10% margin padding like WP plugin
    const sw = bounds.getSouthWest()
    const ne = bounds.getNorthEast()
    const latMargin = (ne.lat - sw.lat) * 0.1
    const lngMargin = (ne.lng - sw.lng) * 0.1
    const paddedBounds = L.latLngBounds(
      [sw.lat - latMargin, sw.lng - lngMargin],
      [ne.lat + latMargin, ne.lng + lngMargin]
    )

    // Mobile results sheet (#856): inset the fit into the band the visitor
    // can see — under the search CTA, above the sheet (toolkit bandPadding,
    // explorer-mobile-sheet.js:686-707). Null outside the sheet layout.
    const band = mobileResults.getFramePadding(mapContainerRef.current)
    mapRef.current.fitBounds(paddedBounds, {
      animate: false,
      ...(band
        ? { paddingTopLeft: [band.left, band.top], paddingBottomRight: [band.right, band.bottom] }
        : { padding: [50, 50] }),
      maxZoom: mapConfig.zoomMax,
    })
  }, [mapConfig.zoomMax, armProgrammaticPan, mobileResults.getFramePadding])

  // PDP link for a map popup: the route with the slug filled in, carrying the
  // visitor's dates/party so the property page opens pre-loaded. Shared by the
  // full property popup and the compact all-pins card (#855).
  // #1714: the base is the shared resolver's link (External Property Pages).
  const buildPdpUrl = useCallback((slug: string | null): string | null => {
    if (!slug) return null
    const link = resolvePropertyLink({ feed: hrefFeed, slug, pdpRoute })
    const f = state.visitorFilters
    const params = new URLSearchParams()
    if (f?.checkin && f?.checkout) {
      params.set('checkin', f.checkin)
      params.set('checkout', f.checkout)
    }
    if (f?.adults) params.set('adults', String(f.adults))
    if (f?.children) params.set('children', String(f.children))
    const guests = (f?.adults ?? 0) + (f?.children ?? 0)
    if (guests > 0) params.set('guests', String(guests))
    return withStayParams(link, params.toString())
  }, [hrefFeed, pdpRoute, state.visitorFilters])

  // Build + place the popup for a property, anchored to a live marker (a
  // plain pin or a spider leaf — the leaf's display position is the anchor).
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const openPopupAt = useCallback((property: ExploreProperty, marker: any) => {
    if (!mapRef.current) return

    closePopup()

    // Build popup — request at the device pixel ratio (capped 2x) so the
    // info-window thumbnail stays crisp on retina displays (matches PropertyCard).
    const imgScale = Math.min(Math.ceil((typeof window !== 'undefined' ? window.devicePixelRatio : 2) || 1), 2)
    const imageUrl = property.images?.[0]
      ? getProxiedImageUrl(property.images[0], 300 * imgScale, 200 * imgScale)
      : ''

    const pdpUrl = buildPdpUrl(property.slug) ?? '#'
    // Card tags (#1819): the popup's type slot shows the card's pills.
    const popupHtml = getPopupHtml(property, imageUrl, pdpUrl, resolveInfowindowIcons(options.map), noDates, options.card?.cardTags, options.card)

    const container = document.createElement('div')
    container.innerHTML = popupHtml
    container.style.position = 'absolute'
    container.style.zIndex = '1000002'
    container.style.pointerEvents = 'auto'

    // Position near marker
    popupOffsetRef.current = { x: 150, y: 250 }
    const markerPoint = mapRef.current.latLngToContainerPoint(marker.getLatLng())
    container.style.left = `${markerPoint.x - popupOffsetRef.current.x}px`
    container.style.top = `${markerPoint.y - popupOffsetRef.current.y}px`

    // Close button handler
    const closeBtn = container.querySelector('.hfse-map-tooltip-close-btn')
    if (closeBtn) {
      closeBtn.addEventListener('click', (e) => {
        e.stopPropagation()
        closePopup()
      })
    }

    mapContainerRef.current?.appendChild(container)
    activePopupRef.current = container
    popupAnchorRef.current = marker
  }, [closePopup, buildPdpUrl, noDates, options.map, options.card?.cardTags, options.card?.showRating, options.card?.ratingMinReviews, options.card?.ratingPlacement])

  // Compact click card for an all-pins dot (#855): a skinny pin carries only
  // title/slug (+ optional price), so the card is title + PDP link — no image
  // fetch. Rides the same absolute-positioned container + anchor-tracking
  // machinery as the full popup, at the card's own smaller offset.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const openPinCard = useCallback((pin: PropertyPin, marker: any) => {
    if (!mapRef.current) return

    closePopup()

    const container = document.createElement('div')
    container.innerHTML =
      `<div class="hfse-allpins-tooltip"><div class="hfse-map-tooltip-content">${pinCardHtml(pin, buildPdpUrl(pin.slug))}</div></div>`
    container.style.position = 'absolute'
    container.style.zIndex = '1000002'
    container.style.pointerEvents = 'auto'

    popupOffsetRef.current = { x: 110, y: 90 }
    const markerPoint = mapRef.current.latLngToContainerPoint(marker.getLatLng())
    container.style.left = `${markerPoint.x - popupOffsetRef.current.x}px`
    container.style.top = `${markerPoint.y - popupOffsetRef.current.y}px`

    mapContainerRef.current?.appendChild(container)
    activePopupRef.current = container
    popupAnchorRef.current = marker
  }, [closePopup, buildPdpUrl])

  // Show popup for a marker at a given index
  const showPopupAtIndex = useCallback((index: number) => {
    if (index < 0 || index >= markersRef.current.length) return
    const { marker, property } = markersRef.current[index]
    openPopupAt(property, marker)
    currentMarkerIndexRef.current = index
    // Focus badge
    const el = marker._icon as HTMLElement | undefined
    if (el) el.classList.add('hfse-map-badge-parent-focus')
  }, [openPopupAt])

  // Hover highlight routing (#854): a clustered pin only has its own icon to
  // invert while individually shown (getVisibleParent === itself — the toolkit
  // rule); a stacked member highlights its stack badge, plus its fanned leaf
  // when the spider is open; a plain pin inverts directly.
  const setHoverHighlight = useCallback((propertyId: string, on: boolean) => {
    const method = on ? 'add' : 'remove'
    const entry = markersRef.current.find(e => e.property.id === propertyId)
    if (!entry) return
    const { marker } = entry

    if (clusterGroupRef.current) {
      let vis = marker
      try { vis = clusterGroupRef.current.getVisibleParent(marker) || marker } catch { vis = marker }
      if (vis === marker) (marker._icon as HTMLElement | undefined)?.classList[method]('hfse-map-badge-parent-focus')
      return
    }

    const stackKey = memberStackKeyRef.current.get(propertyId)
    if (stackKey) {
      const badge = stackMarkersRef.current.get(stackKey)
      ;(badge?._icon as HTMLElement | undefined)?.classList[method]('hfse-map-badge-parent-focus')
      if (fanRef.current?.key === stackKey) {
        fanRef.current.leaves.forEach(({ marker: leaf, id }) => {
          if (id === propertyId) (leaf._icon as HTMLElement | undefined)?.classList[method]('hfse-map-badge-parent-focus')
        })
      }
      return
    }

    ;(marker._icon as HTMLElement | undefined)?.classList[method]('hfse-map-badge-parent-focus')
  }, [])

  // Focus a marker from the property grid (hover)
  const focusMarker = useCallback((propertyId: string) => {
    if (!mapRef.current) return

    const index = markersRef.current.findIndex(e => e.property.id === propertyId)
    if (index < 0) return

    const { marker } = markersRef.current[index]
    setHoverHighlight(propertyId, true)

    // Pan to marker if outside 10% margin viewport — except on hover-capable
    // devices, where card hover must not move the map (hoverPanLock, #1046).
    if (hoverPanLocked()) return
    const markerLatLng = marker.getLatLng()
    const mapBounds = mapRef.current.getBounds()
    const sw = mapBounds.getSouthWest()
    const ne = mapBounds.getNorthEast()
    const latMargin = (ne.lat - sw.lat) * 0.1
    const lngMargin = (ne.lng - sw.lng) * 0.1
    const smallerBounds = L.latLngBounds(
      [sw.lat + latMargin, sw.lng + lngMargin],
      [ne.lat - latMargin, ne.lng - lngMargin]
    )

    if (!smallerBounds.contains(markerLatLng)) {
      armProgrammaticPan()
      mapRef.current.panTo(markerLatLng)
    }
  }, [armProgrammaticPan, setHoverHighlight])

  // Blur a marker (mouse leave)
  const blurMarker = useCallback((propertyId: string) => {
    setHoverHighlight(propertyId, false)
  }, [setHoverHighlight])

  // Register map actions on the context
  useEffect(() => {
    if (!leafletLoaded) return
    registerMapActions({ focusMarker, blurMarker, fitBounds })
    return () => registerMapActions(null)
  }, [leafletLoaded, focusMarker, blurMarker, fitBounds, registerMapActions])

  // Update markers when properties change
  useEffect(() => {
    if (!mapRef.current || !leafletLoaded) return
    const map = mapRef.current

    // Tear down the previous render: the spider first (its collapse re-adds
    // the stack badge it hid, which the loops below then remove cleanly).
    spiderRef.current?.dispose()
    spiderRef.current = null
    markersRef.current.forEach(({ marker }) => marker.remove())
    markersRef.current = []
    extraMarkersRef.current.forEach((marker) => marker.remove())
    extraMarkersRef.current = []
    stackMarkersRef.current.forEach((marker) => marker.remove())
    stackMarkersRef.current = new Map()
    stackGroupsRef.current = new Map()
    memberStackKeyRef.current = new Map()
    if (clusterGroupRef.current) clusterGroupRef.current.clearLayers()
    closePopup()


    // Toolkit map-stacking (#854): cluster-primary routes EVERY pin into a
    // markercluster group; stacked-badge mode collapses only exactly
    // co-located pins. Until the markercluster plugin has loaded, pins render
    // plain — the effect re-runs when clusterReady flips.
    const clusterActive = stacking.clusterMode && clusterReady
    const stackActive = stacking.enabled && !stacking.clusterMode

    if (clusterActive && !clusterGroupRef.current) {
      clusterGroupRef.current = L.markerClusterGroup({
        showCoverageOnHover: false,
        maxClusterRadius: 60,
        spiderfyOnMaxZoom: true,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        iconCreateFunction: (cluster: any) => {
          const { html, size } = clusterIconHtml(cluster.getChildCount())
          return L.divIcon({ html, className: 'hfse-cluster-wrap', iconSize: [size, size] })
        },
      })
      map.addLayer(clusterGroupRef.current)
    }
    if (!clusterActive && clusterGroupRef.current) {
      map.removeLayer(clusterGroupRef.current)
      clusterGroupRef.current = null
    }

    const stackGroups = stackActive ? buildStackGroups(properties) : new Map<string, string[]>()
    stackGroupsRef.current = stackGroups
    for (const [key, ids] of stackGroups) {
      for (const id of ids) memberStackKeyRef.current.set(id, key)
    }
    const propsById = new Map(properties.map((p) => [p.id, p]))

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const clusterBatch: any[] = []
    properties.forEach((property) => {
      if (!property.location?.lat || !property.location?.lng) return

      const lat = property.location.lat
      const lng = property.location.lng

      const badgeHtml = getMarkerBadgeHtml(property, showPrice, noDates)
      const marker = L.marker([lat, lng], {
        icon: L.divIcon({
          html: badgeHtml,
          iconSize: [60, 40],
          iconAnchor: [30, 20],
          className: 'hfse-map-marker-wrapper',
        }),
      })

      marker.on('click', () => {
        const idx = markersRef.current.findIndex(e => e.property.id === property.id)
        if (idx >= 0) {
          // Toggle: clicking same marker closes popup
          if (currentMarkerIndexRef.current === idx) {
            closePopup()
          } else {
            showPopupAtIndex(idx)
          }
        }
      })

      if (clusterActive) {
        clusterBatch.push(marker)
      } else if (!memberStackKeyRef.current.has(property.id)) {
        marker.addTo(map)
      }
      // Stacked members stay off the map — their pins render only in the fan.
      markersRef.current.push({ marker, property })
    })

    // All-pins extras (#855): every home in the filtered set beyond the loaded
    // page as a lightweight dot — under the price badges (zIndexOffset) so a
    // dot never occludes a real pin, folded into the cluster group when
    // clustering is on. As pages load in, matching dots drop out (the dedupe
    // against renderedIds re-runs with every rebuild).
    if (pins.length) {
      const renderedIds = new Set(properties.map((p) => p.id))
      extraPinsFor(pins, renderedIds).forEach((pin) => {
        const dot = L.marker([pin.lat, pin.lng], {
          icon: L.divIcon({
            html: dotPinHtml(pin),
            className: 'hfse-allpins-marker',
            iconSize: [DOT_PIN_SIZE, DOT_PIN_SIZE],
            iconAnchor: [DOT_PIN_SIZE / 2, DOT_PIN_SIZE / 2],
          }),
          interactive: true,
          zIndexOffset: -200,
        })
        dot.on('click', () => {
          // Toggle: clicking the same dot closes its card
          if (popupAnchorRef.current === dot && activePopupRef.current) {
            closePopup()
          } else {
            openPinCard(pin, dot)
          }
        })
        if (clusterActive) {
          clusterBatch.push(dot)
        } else {
          dot.addTo(map)
        }
        extraMarkersRef.current.push(dot)
      })
    }

    if (clusterActive && clusterBatch.length) clusterGroupRef.current.addLayers(clusterBatch)

    // Stacked badges + the spiderfy controller (stacked-badge mode only)
    if (stackActive && stackGroups.size) {
      for (const [key, ids] of stackGroups) {
        const groupProps = ids.map((id) => propsById.get(id)).filter((p): p is ExploreProperty => !!p)
        const priceRange = showPrice ? getPriceRangeText(groupProps) : ''
        const anchor = parseStackKey(key)
        const badge = L.marker([anchor.lat, anchor.lng], {
          icon: L.divIcon({
            // Same box/anchor as the plain pins: the .hfse-map-badge translate
            // centers the pill on the coordinate.
            html: stackedBadgeHtml(priceRange, ids.length),
            className: 'hfse-stack-wrapper',
            iconSize: [60, 40],
            iconAnchor: [30, 20],
          }),
          interactive: true,
          zIndexOffset: 500,
        })
        badge.on('click', () => spiderRef.current?.toggle(key))
        badge.addTo(map)
        stackMarkersRef.current.set(key, badge)
      }

      const ensureSpiderPane = () => {
        if (!map.getPane('hfseSpiderPane')) {
          map.createPane('hfseSpiderPane')
          map.getPane('hfseSpiderPane').style.zIndex = 650
        }
      }

      spiderRef.current = createSpiderfyController(map, stacking.fadeOpacity, {
        renderFan: (key) => {
          const ids = stackGroupsRef.current.get(key)
          if (!ids?.length) return null
          ensureSpiderPane()
          const a = parseStackKey(key)
          const anchorLatLng = L.latLng(a.lat, a.lng)
          const centerPt = map.latLngToContainerPoint(anchorLatLng)
          const offsets = getSpiderOffsets(ids.length)

          const center = L.marker(anchorLatLng, {
            icon: L.divIcon({ html: '<div class="hfse-spider-center"></div>', className: 'hfse-spider-center-wrap', iconSize: [16, 16], iconAnchor: [8, 8] }),
            interactive: true,
            pane: 'hfseSpiderPane',
          }).addTo(map)
          center.on('click', () => spiderRef.current?.collapse())

          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const leaves: { marker: any; id: string }[] = []
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const legs: any[] = []
          ids.forEach((id, i) => {
            const property = propsById.get(id)
            if (!property) return
            const target = map.containerPointToLatLng(L.point(centerPt.x + offsets[i].x, centerPt.y + offsets[i].y))
            const leaf = L.marker(anchorLatLng, {
              icon: L.divIcon({ html: getMarkerBadgeHtml(property, showPrice, noDates), iconSize: [60, 40], iconAnchor: [30, 20], className: 'hfse-spider-leaf' }),
              interactive: true,
              pane: 'hfseSpiderPane',
            }).addTo(map)
            // Slide from the anchor to the fan position (toolkit animation).
            requestAnimationFrame(() => {
              const el = leaf._icon as HTMLElement | undefined
              el?.classList.add('hfse-spider-animating')
              leaf.setLatLng(target)
              setTimeout(() => el?.classList.remove('hfse-spider-animating'), 350)
            })
            leaf.on('click', () => openPopupAt(property, leaf))
            const leg = L.polyline([anchorLatLng, target], {
              weight: 1.5,
              opacity: 0.4,
              dashArray: '4 2',
              color: stacking.connector,
              className: 'hfse-spider-leg',
              pane: 'hfseSpiderPane',
            }).addTo(map)
            leaves.push({ marker: leaf, id })
            legs.push(leg)
          })
          fanRef.current = { key, leaves, legs }
          return () => {
            leaves.forEach(({ marker }) => { marker.off(); map.removeLayer(marker) })
            legs.forEach((l) => map.removeLayer(l))
            center.off()
            map.removeLayer(center)
            fanRef.current = null
            closePopup()
          }
        },
        repositionFan: (key) => {
          const fan = fanRef.current
          if (!fan || fan.key !== key) return
          const a = parseStackKey(key)
          const anchorLatLng = L.latLng(a.lat, a.lng)
          const centerPt = map.latLngToContainerPoint(anchorLatLng)
          const offsets = getSpiderOffsets(fan.leaves.length)
          fan.leaves.forEach(({ marker }, i) => {
            const target = map.containerPointToLatLng(L.point(centerPt.x + offsets[i].x, centerPt.y + offsets[i].y))
            marker.setLatLng(target)
            fan.legs[i]?.setLatLngs([anchorLatLng, target])
          })
        },
        isAnchorVisible: (key) => {
          const a = parseStackKey(key)
          return map.getBounds().contains([a.lat, a.lng])
        },
        setFade: (opacity) => {
          const pane = map.getPane('markerPane')
          if (pane) pane.style.opacity = opacity == null ? '' : String(opacity)
        },
        onOpen: (key) => {
          const badge = stackMarkersRef.current.get(key)
          if (badge && badge._map) map.removeLayer(badge)
        },
        onClose: (key) => {
          const badge = stackMarkersRef.current.get(key)
          if (badge && !badge._map) badge.addTo(map)
        },
      })
    }

    // Fit bounds: always on first load (mapBounds null), or when search-on-move is off
    if (markersRef.current.length > 0 && !boundsApplied(state)) {
      map.invalidateSize()
      fitBounds()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [properties, pins, leafletLoaded, closePopup, showPopupAtIndex, openPopupAt, openPinCard, fitBounds, stacking, clusterReady, showPrice, noDates])

  // Viewport-fit / split settings change the container size via CSS — Leaflet
  // needs an invalidateSize, guarded so the resulting moveend isn't treated as
  // a user move (refilter-guard parity, #852).
  useEffect(() => {
    if (!leafletLoaded || !mapRef.current) return
    armProgrammaticPan()
    const gen = programmaticPanSeqRef.current
    mapRef.current.invalidateSize()
    // A no-size-change invalidation emits no moveend, which would leave the
    // suppression latched and swallow the NEXT genuine gesture's update —
    // bounded fallback clear (debounce is 500ms). Generation-guarded: if a
    // NEWER programmatic operation (fitBounds, marker pan) armed suppression
    // after this invalidation, the stale timer must not strip it (#858 r5).
    const timer = setTimeout(() => {
      if (programmaticPanSeqRef.current === gen) programmaticPanRef.current = false
    }, 700)
    return () => clearTimeout(timer)
  }, [leafletLoaded, options.map?.viewportFit, options.map?.splitPct, armProgrammaticPan])

  // Mobile results sheet (#856): entering/leaving the sheet layout swaps the
  // container between the stacked 400px box and the full-bleed fixed backdrop
  // — a size change Leaflet cannot see on its own. Re-measure, then re-frame
  // into the visible band (same first-load / search-off gate as the marker
  // effect) so the pins land above the sheet, not under it. Same
  // generation-guarded suppression as the viewport-fit effect above.
  useEffect(() => {
    if (!leafletLoaded || !mapRef.current) return
    armProgrammaticPan()
    const gen = programmaticPanSeqRef.current
    mapRef.current.invalidateSize()
    if (markersRef.current.length > 0 && !boundsApplied(state)) fitBounds()
    const timer = setTimeout(() => {
      if (programmaticPanSeqRef.current === gen) programmaticPanRef.current = false
    }, 700)
    return () => clearTimeout(timer)
  // Keyed on the layout switch only — re-running on every fitBounds identity
  // or search-state change would re-frame under the visitor's own panning.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leafletLoaded, mobileResults.active])

  // Handle search on move toggle
  const handleSearchToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
    searchControls.onToggle(e.target.checked, getBoundsBox())
  }

  // Map control handlers — zoom buttons count as genuine user gestures for the
  // first-gesture activation contract (#852). Like the drag/wheel/touch path,
  // they must clear any pending programmatic suppression (e.g. the
  // invalidateSize window) so their own moveend is not discarded (#858 round 4).
  const handleZoomIn = () => { programmaticPanRef.current = false; searchControls.onUserGesture(); mapRef.current?.zoomIn() }
  const handleZoomOut = () => { programmaticPanRef.current = false; searchControls.onUserGesture(); mapRef.current?.zoomOut() }

  const handleFullscreen = () => {
    const el = mapContainerRef.current?.parentElement
    if (!el) return
    if (document.fullscreenElement) {
      document.exitFullscreen()
    } else {
      el.requestFullscreen()
    }
  }

  return (
    <div className="hfse-map-container" style={stacking.enabled ? (stackingCssVars(stacking) as React.CSSProperties) : undefined}>
      <div className="hfse-map" ref={mapContainerRef} style={{ width: '100%', height: '100%' }} onPointerDown={(e) => beginMapDragGuard(e.currentTarget)}>
        {!leafletLoaded && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            Loading map...
          </div>
        )}
      </div>

      {/* Mapbox requires its official logo, unaltered, in addition to text
          attribution when its tiles render through Leaflet (#858). The logo
          itself is the CSS background of .hfse-mapbox-logo. */}
      {leafletLoaded && resolvedTileProvider === 'mapbox' && (
        <a
          className="hfse-mapbox-logo"
          href="https://www.mapbox.com/"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Mapbox logo"
        />
      )}

      {/* Custom Map Controls */}
      {leafletLoaded && (
        <div className="hfse-map-controls" style={{ ...(options.map.controlBg ? { '--hfse-map-control-bg': options.map.controlBg } : {}), ...(options.map.controlColor ? { '--hfse-map-control-color': options.map.controlColor } : {}) } as React.CSSProperties}>
          <MapChrome
            onZoomIn={handleZoomIn}
            onZoomOut={handleZoomOut}
            onFullscreen={handleFullscreen}
            showFullscreen={options.map?.showFullscreen !== false}
          />

          {/* Top-center: search on move (concealed while first-gesture armed, #852) */}
          {searchControls.searchOnMove && !searchControls.concealSearchToggle && (
            <span className="map-control control-search">
              <div className="map-control-input-container">
                <input
                  type="checkbox"
                  id="hr-map-search-checkbox"
                  className="map-control-checkbox"
                  checked={searchControls.searchActive}
                  onChange={handleSearchToggle}
                />
                <label htmlFor="hr-map-search-checkbox" className="map-control-label">
                  Search as I move the map
                </label>
              </div>
            </span>
          )}

          {searchControls.showSearchArea && (
            <button
              type="button"
              className="hfse-map-search-area-btn"
              onClick={() => searchControls.onSearchAreaClick(getBoundsBox())}
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
                <circle cx="11" cy="11" r="7" />
                <path d="m21 21-4.35-4.35" />
              </svg>
              Search this area
            </button>
          )}
        </div>
      )}
    </div>
  )
}
